import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultWMS2040_R01DetailDto, WMS2040_R01DetailDto } from './Dto_WMS2040_R01';
import { useWMS2040_R01Store } from './Store_WMS2040_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'WMS2040_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS2040_R01DetailDto> | null>;
}

export const DetailTable_WMS2040_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		WMS2040_R01DetailList,
		selectedWMS2040_R01DetailList,
		setSelectedWMS2040_R01DetailList,
		deletedWMS2040_R01DetailList,
		initSelectedWMS2040_R01DetailList,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useWMS2040_R01Store();

	const { inoutClassList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: WMS2040_R01DetailList,
		defaultGridData: defaultWMS2040_R01DetailDto,
		selectedRow: selectedWMS2040_R01DetailList
			? WMS2040_R01DetailList.find((item) => String(item.valueIndex) === selectedWMS2040_R01DetailList)
			: undefined,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		txDt: '입고일자',
		warehouseNm: '수불창고명',
		partnerNm: '거래처명',
		inoutClass: '수불유형구분',
		inQty: '입고량',
		outQty: '출고량',
		balanceQty: '재고량',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'valueIndex')(params), []);

	const baseColumnDefs: ColDef<WMS2040_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '입고일자', colId: 'txDt', field: 'txDt', width: 120,
			editable: false,
			valueFormatter: (params) => {
				if (params.data?.txDtDisplay) {
					return params.data.txDtDisplay;
				}
				if (params.value) {
					const date = new Date(params.value);
					const year = date.getFullYear();
					const month = String(date.getMonth() + 1).padStart(2, '0');
					const day = String(date.getDate()).padStart(2, '0');
					return `${year}-${month}-${day}`;
				}
				return '';
			},
		},
		{
			headerName: '수불창고명', colId: 'warehouseNm', field: 'warehouseNm', width: 100,
			editable: false,
		},
		{
			headerName: '수불유형구분', colId: 'inoutClass', field: 'inoutClass', width: 120,
			editable: false,
			valueFormatter: (params) => {
				if (!params.value) return '';
				const codeItem = inoutClassList.find((item) => item.minorCd === params.value);
				return codeItem?.codeNm || params.value;
			},
		},
		{
			headerName: '거래처명', colId: 'partnerNm', field: 'partnerNm', width: 120,
			editable: false,
		},
		{
			headerName: '입고량', colId: 'inQty', field: 'inQty', width: 100,
			editable: false,
			valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
			cellStyle: (p) => getCellStyle(p, 'inQty'),
		},
		{
			headerName: '출고량', colId: 'outQty', field: 'outQty', width: 100,
			editable: false,
			valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
			cellStyle: (p) => getCellStyle(p, 'outQty'),
		},
		{
			headerName: '재고량', colId: 'balanceQty', field: 'balanceQty', width: 100,
			editable: false,
			valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
			cellStyle: (p) => getCellStyle(p, 'balanceQty'),
		},
	], [headerNames, inoutClassList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS2040_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS2040_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => {
		const totals = WMS2040_R01DetailList.reduce((acc, item) => ({
			inQty: acc.inQty + (Number(item.inQty) || 0),
			outQty: acc.outQty + (Number(item.outQty) || 0),
		}), { inQty: 0, outQty: 0 });

		const balanceQty = totals.inQty - totals.outQty;

		return [{
			valueIndex: '',
			rowIndex: '',
			txDt: '',
			inoutClass: '',
			inQty: totals.inQty,
			outQty: totals.outQty,
			balanceQty,
			editable: false,
		}];
	}, [WMS2040_R01DetailList]);

	const normalizedSelectedIds = useMemo(() => {
		if (!selectedWMS2040_R01DetailList) return [];
		if (/^\d+$/.test(String(selectedWMS2040_R01DetailList))) {
			return [Number(selectedWMS2040_R01DetailList)];
		}
		return [];
	}, [selectedWMS2040_R01DetailList]);

	const rowClassRules = useMemo(() =>
		createRowClassRules<WMS2040_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: normalizedSelectedIds,
			deletedList: deletedWMS2040_R01DetailList,
		}), [createRowClassRules, normalizedSelectedIds, deletedWMS2040_R01DetailList]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS2040_R01DetailDto>) => {
		if (e.node?.rowPinned === 'bottom') return;
		const row = e.data;
		if (!row || typeof row.valueIndex !== 'number') return;

		const id = String(row.valueIndex);
		setSelectedWMS2040_R01DetailList(selectedWMS2040_R01DetailList === id ? undefined : id);
	}, [selectedWMS2040_R01DetailList, setSelectedWMS2040_R01DetailList]);

	return (
		<GridContextWrapper<WMS2040_R01DetailDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedWMS2040_R01DetailList}
			defaultRowData={defaultWMS2040_R01DetailDto}
			allSelected={() => {}}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<WMS2040_R01DetailDto>
				ref={gridRef}
				rowData={WMS2040_R01DetailList}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	);
};

export default DetailTable_WMS2040_R01;
